import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MaterialsService } from '../../../../services/materials.service';
import { DesignService } from '../../../../services/design.service';
import { MaterialListRowDto } from '@models/material-list-row-dto.interface';
import { MaterialListQuery } from '@models/material-list-query.interface';
import { FormulationComponentDto } from '@models/job-request-iterations-dto.interface';
import { SearchParameters } from '@shared/models/search-parameters.model';
import { MatIconModule } from '@angular/material/icon';
import { EditMaterialDialogComponent } from '../../../inventory/inventory-dialog/edit-material-dialog/edit-material-dialog.component';

export interface AddMudCheckMaterialDialogData {
  jobRequestId: number;
}

@Component({
  selector: 'app-add-mud-check-material-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatDialogModule, MatButtonModule, MatProgressSpinnerModule, MatIconModule],
  templateUrl: './add-mud-check-material-dialog.component.html',
  styleUrl: './add-mud-check-material-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddMudCheckMaterialDialogComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<AddMudCheckMaterialDialogComponent>);
  protected readonly data = inject<AddMudCheckMaterialDialogData>(MAT_DIALOG_DATA);
  private readonly materialsService = inject(MaterialsService);
  private readonly designService = inject(DesignService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  protected isLoading = signal(true);
  protected isSearching = signal(false);
  protected isSaving = signal(false);
  protected errorMessage = signal<string | null>(null);

  protected materials = signal<MaterialListRowDto[]>([]);
  protected selectedMaterial = signal<MaterialListRowDto | null>(null);

  protected filterControl = this.fb.control<string | null>(null);

  private readonly dialogMaterials = inject(MatDialog);

  ngOnInit(): void {
    this.loadMaterials('');
  }

  private loadMaterials(filter: string): void {
    this.isSearching.set(true);
    this.materialsService
      .searchMaterials(this.buildSearchParams(filter))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          this.materials.set(result.items);
          this.isSearching.set(false);
          this.isLoading.set(false);
        },
        error: () => {
          this.isSearching.set(false);
          this.isLoading.set(false);
        },
      });
  }

  private buildSearchParams(filter: string): SearchParameters<MaterialListQuery> {
    return {
      query: { filter: filter || null },
      pageIndex: 0,
      pageSize: 50,
    };
  }

  protected onSearch(): void {
    this.loadMaterials(this.filterControl.value ?? '');
  }

  protected onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.onSearch();
    }
  }

  protected onSelectMaterial(material: MaterialListRowDto): void {
    this.selectedMaterial.set(this.selectedMaterial()?.id === material.id ? null : material);
  }

  protected onAdd(): void {
    const material = this.selectedMaterial();
    if (!material || this.isSaving()) return;

    this.errorMessage.set(null);
    this.isSaving.set(true);
    this.designService.setMudCheckMaterial(this.data.jobRequestId, material.id).subscribe({
      next: (component: FormulationComponentDto) => {
        this.isSaving.set(false);
        this.dialogRef.close(component);
      },
      error: () => {
        this.isSaving.set(false);
        this.errorMessage.set('Failed to add material. Please try again.');
      },
    });
  }

  protected onAddNewFluid(): void {
      this.dialogMaterials
            .open(EditMaterialDialogComponent, {
              width: '750px',
              minWidth: '750px',
              maxHeight: '90vh',
              data: {},
            })
            .afterClosed()
            .subscribe((newId: number | undefined) => {
              if (newId) {
                this.loadMaterials('');
              }
            });
  }

  protected onCancel(): void {
    this.dialogRef.close(null);
  }
}
